/**
 * Closes the cart drawer on an outside click.
 *
 * Horizon's <theme-drawer> only gets a native backdrop (click-outside-to-close)
 * below the 990px breakpoint, where it opens as a full-screen modal dialog —
 * above that it's a non-modal docked panel with no light-dismiss, by design
 * for sidebar-style drawers that stay open while browsing. kaw-cart-drawer.css
 * turns the cart drawer into a small anchored dropdown at that same 990px
 * breakpoint instead, so it needs the same click-outside-to-close behavior
 * the reference site's mini-cart has. The drawer's own open/close mechanism
 * (theme-drawer.js) is untouched — this just calls its public close() method.
 */
document.addEventListener('click', (event) => {
  if (!window.matchMedia('(min-width: 990px)').matches) return;

  const drawer = /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
    document.getElementById('cart-drawer')
  );
  if (!drawer || !drawer.hasAttribute('open')) return;
  if (!(event.target instanceof Node)) return;
  if (drawer.contains(event.target)) return;

  // The click that OPENS the drawer (on the trigger button, via Horizon's
  // own on:click="#cart-drawer/toggle") also bubbles up to this document
  // listener, which by then already sees the drawer as open — without this
  // check it would immediately treat its own opening click as "outside" and
  // close the drawer right back, i.e. the exact open-then-instantly-close
  // flash this guards against.
  const trigger = document.querySelector('.kaw-utility-bar [aria-controls="cart-drawer"]');
  if (trigger?.contains(event.target)) return;

  drawer.close?.();
});

/**
 * Anchors the (desktop-only) dropdown to the real on-screen position of the
 * cart trigger button instead of a hardcoded header-height guess in CSS —
 * the utility bar's rendered height can vary (announcement bar, text
 * wrapping at narrower desktop widths), so a fixed calc() drifts. Sets CSS
 * custom properties read by kaw-cart-drawer.css; falls back to that CSS
 * calc() until the first open (or on very old browsers without :has()).
 */
(() => {
  const drawer = document.getElementById('cart-drawer');
  // Scoped to .kaw-utility-bar specifically: Horizon's own header-actions.liquid
  // renders a native cart trigger with the same aria-controls="cart-drawer",
  // and it can still be present (hidden) in the DOM — an unscoped selector risks
  // matching that zero-size element instead of the visible one in the utility bar.
  const trigger = document.querySelector('.kaw-utility-bar [aria-controls="cart-drawer"]');
  if (!drawer || !trigger) return;

  // Gap between the trigger's bottom edge and the dialog's top edge — big
  // enough for the CSS pointer triangle plus a few px of air above the icon
  // it points to.
  const ARROW_GAP = 8;

  // Nudges the dialog a bit further right than a strict flush alignment
  // with the trigger's right edge.
  const RIGHT_NUDGE = 12;

  const positionDrawer = () => {
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    const dialog = drawer.querySelector('dialog');
    if (!dialog) return;

    const triggerRect = trigger.getBoundingClientRect();
    // Guard against a hidden/not-yet-laid-out trigger (zero rect) — writing
    // that would pin the drawer to the top-left corner. Leave the CSS
    // calc() fallback in place instead.
    if (triggerRect.width === 0 && triggerRect.height === 0) return;

    // Anchored to the trigger itself, not the full two-row header — the
    // dropdown sits right under the cart icon (matching the reference
    // site's mini-cart) and overlaps the logo/nav row below it rather than
    // clearing it; the pointer triangle bridges that back to the icon
    // visually. It renders above that row regardless (z-index: 1000 vs.
    // kaw-header's 999).
    dialog.style.setProperty('--kaw-cart-drawer-top', `${Math.round(triggerRect.bottom + ARROW_GAP)}px`);
    dialog.style.setProperty('--kaw-cart-drawer-right', `${Math.round(window.innerWidth - triggerRect.right - RIGHT_NUDGE)}px`);
    // The arrow-offset math below still assumes the dialog's right edge is
    // flush with the trigger's right edge, so RIGHT_NUDGE is added back here
    // to keep the pointer triangle centered under the icon even though the
    // dialog itself now sits RIGHT_NUDGE further right than that.
    dialog.style.setProperty('--kaw-cart-drawer-arrow-offset', `${Math.round(triggerRect.width / 2 + RIGHT_NUDGE)}px`);
  };

  // Computed eagerly, not on theme-drawer:open — that event fires in the
  // same tick the drawer starts its slide-in animation, which reads the
  // dialog's `right` as its (unanimated) end value. Setting the custom
  // properties that late races the animation's first frame: the browser can
  // sample a stale value, producing a visible jump/slide across the screen.
  // Positioning ahead of time (page load + resize) means the value is
  // already stable before any click/animation happens.
  requestAnimationFrame(positionDrawer);
  window.addEventListener('resize', positionDrawer);
  drawer.addEventListener('theme-drawer:open', positionDrawer);
})();
